import React, { useState, useEffect, useRef } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Character, 
  CharacterFeatureSource, 
  CharacterSheetInterface,
  CharacterFeature,
  LANGUAGES,
} from "@dndModels";

import DisplayObjects from "@dndComponents/display/DisplayObjects";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import CharacterFeatureFromSource from "@dndComponents/character/features/CharacterFeatureFromSource";
import FloatMenu from "@components/input/menus/FloatMenu";
import LoadingBox from "@components/input/LoadingBox";
import CharacterLanguageCustomization from "@dndComponents/character/misc/CharacterLanguageCustomization";

interface Props {
  char: CharacterSheetInterface;
  disabled: boolean;
}

export default function CharacterLanguages({
  char,
  disabled,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("Features");
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const recalculated = () => {
    // console.log("Proficiencies");
    setReloading(true);
  }

  // eslint-disable-next-line
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      char.subscription_holder.subscribe("Recalculated", mounted, recalculated);
      char.subscription_holder.subscribe("Skill", mounted, recalculated);
    }
  });

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderLanguageFeatures = () => {
    const language_features = char.mind.language_features;
    const use_features: CharacterFeature[] = [];
    for (const cf of language_features) {
      if (use_features.filter(o => o.cfb_id === cf.cfb_id).length === 0) {
        use_features.push(cf);
      }
    }
    return (
      <>
        <Grid
          className="header centered"
          size={{
            xs: 12
          }}>
          Language Features
        </Grid>
        { use_features.sort((a,b) => { return a.name.localeCompare(b.name) }).map((char_feature, key) => {
          const source = char.char_feature_sources.find(o => 
            o.true_id === char_feature.source_true_id);
          if (source) {
            return (
              <Grid
                key={key}
                size={{
                  xs: 12
                }}>
                <CharacterFeatureFromSource
                  char={char}
                  obj={char_feature}
                  source={source}
                />
              </Grid>
            );
          }
          return null;
        })}
      </>
    );
  }

  const renderLanguageCustomizations = () => {
    return (
      <>
        <Grid
          className="header centered"
          size={{
            xs: 12
          }}>
          { view }
        </Grid>
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 12
          }}>
          { LANGUAGES.map(language => {
            return (
              <Grid
                key={language._id}
                size={{
                  xs: 12
                }}>
                <CharacterLanguageCustomization 
                  char={char} 
                  language={language} 
                />
              </Grid>
            );
          }) }
        </Grid>
      </>
    );
  }

  const renderView = () => {
    switch(view) {
      // case "Current": return renderCurrent();
      case "Features": return renderLanguageFeatures();
      default: return renderLanguageCustomizations();
    }
  }

  const renderControls = () => {
    return (
      <Grid
        container
        spacing={0}
        direction="row"
        size={{
          xs: 12
        }}>
        <Grid
          className="centered header"
          size={{
            xs: 12
          }}>
          Manage Languages
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <FloatMenu
            options={[
              "Features",
              "Customize Languages",
            ]}
            selected={view}
            onSelect={setView}
          />
        </Grid>
        { renderView() }
      </Grid>
    );
  }
  
  if (!fetched) {
    return (<LoadingBox />);
  } else {
    // console.log(char.languages_known);
    return (
      <Grid container spacing={0} direction="row" className="curved_border bg_black_transp">
        <Grid
          className={`centered header ${ disabled ? "" : "clickable" }`}
          onClick={() => {
            if (!disabled) {
              setOpen(true); 
            } 
          }}
          size={{
            xs: 12
          }}>
          Languages
        </Grid>
        { !reloading && char.mind.languages_known.length > 0 &&
          <>
            <Grid
              style={{
                fontSize: "11px", paddingLeft: "4px"
              }}
              size={{
                xs: 12
              }}>
              <DisplayObjects type="language" ids={char.mind.languages_known} />
            </Grid>
          </>
        }
        <ModalOrDrawer
          open={ open } 
          onClose={() => {
            setOpen(false);
            char.subscription_holder.modalClosed();
          }}>
          { renderControls() }
        </ModalOrDrawer>
      </Grid>
    );
  }
}
